import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { Json } from '@/types/database.types'

interface MaintenanceModeSettings {
  enabled: boolean
  message: string
}

interface LiveTickerSettings {
  enabled: boolean
  url: string
  text: string
}

interface WithdrawalLimitsSettings {
  min_amount_pence: number
  max_amount_pence: number
}

interface HeroContentSettings {
  title: string
  description: string
}

interface FeaturedPartnerSettings {
  enabled: boolean
  mode: 'auto' | 'manual'
  manual_partner_id: string | null
}

interface EmailLogoSettings {
  url: string | null
}

interface SystemSettings {
  maintenance_mode: MaintenanceModeSettings
  live_ticker: LiveTickerSettings
  withdrawal_limits: WithdrawalLimitsSettings
  hero_content: HeroContentSettings
  featured_partner: FeaturedPartnerSettings
  email_logo: EmailLogoSettings
}

interface SystemSettingsReturn {
  settings: SystemSettings | null
  loading: boolean
  updateSetting: (key: string, value: Json) => Promise<void>
  maintenanceMode: MaintenanceModeSettings | undefined
  liveTicker: LiveTickerSettings | undefined
  withdrawalLimits: WithdrawalLimitsSettings | undefined
  heroContent: HeroContentSettings | undefined
  featuredPartner: FeaturedPartnerSettings | undefined
  emailLogo: EmailLogoSettings | undefined
}

export function useSystemSettings(): SystemSettingsReturn {
  const [settings, setSettings] = useState<SystemSettings | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadSettings()

    // Subscribe to real-time changes in system_settings table
    const subscription = supabase
      .channel('system_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_settings'
        },
        () => {
          // Reload settings when any change occurs
          loadSettings()
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['maintenance_mode', 'live_ticker', 'withdrawal_limits', 'hero_content', 'featured_partner', 'email_logo'])

      if (error) throw error

      // Transform array to object with typed values
      let maintenanceMode: MaintenanceModeSettings | undefined
      let liveTicker: LiveTickerSettings | undefined
      let withdrawalLimits: WithdrawalLimitsSettings | undefined
      let heroContent: HeroContentSettings | undefined
      let featuredPartner: FeaturedPartnerSettings | undefined
      let emailLogo: EmailLogoSettings | undefined

      data?.forEach((item) => {
        if (item.setting_key === 'maintenance_mode' && item.setting_value && typeof item.setting_value === 'object' && !Array.isArray(item.setting_value)) {
          maintenanceMode = item.setting_value as unknown as MaintenanceModeSettings
        } else if (item.setting_key === 'live_ticker' && item.setting_value && typeof item.setting_value === 'object' && !Array.isArray(item.setting_value)) {
          liveTicker = item.setting_value as unknown as LiveTickerSettings
        } else if (item.setting_key === 'withdrawal_limits' && item.setting_value && typeof item.setting_value === 'object' && !Array.isArray(item.setting_value)) {
          withdrawalLimits = item.setting_value as unknown as WithdrawalLimitsSettings
        } else if (item.setting_key === 'hero_content' && item.setting_value && typeof item.setting_value === 'object' && !Array.isArray(item.setting_value)) {
          heroContent = item.setting_value as unknown as HeroContentSettings
        } else if (item.setting_key === 'featured_partner' && item.setting_value && typeof item.setting_value === 'object' && !Array.isArray(item.setting_value)) {
          featuredPartner = item.setting_value as unknown as FeaturedPartnerSettings
        } else if (item.setting_key === 'email_logo' && item.setting_value && typeof item.setting_value === 'object' && !Array.isArray(item.setting_value)) {
          emailLogo = item.setting_value as unknown as EmailLogoSettings
        }
      })

      // Set settings (only maintenance_mode and live_ticker are required)
      if (maintenanceMode && liveTicker) {
        setSettings({
          maintenance_mode: maintenanceMode,
          live_ticker: liveTicker,
          withdrawal_limits: withdrawalLimits || { min_amount_pence: 10000, max_amount_pence: 1000000 },
          hero_content: heroContent || {
            title: 'Win Premium Baby Gear Instantly',
            description: 'Enter our instant win competitions for a chance to win iCandy prams, car seats, and cash prizes. Over 1,900 instant wins available now.'
          },
          featured_partner: featuredPartner || {
            enabled: true,
            mode: 'auto',
            manual_partner_id: null
          },
          email_logo: emailLogo || {
            url: null
          }
        })
      } else {
        throw new Error('Missing required settings')
      }
    } catch (error) {
      console.error('Error loading system settings:', error)
      // Set empty settings on error to prevent blocking the app
      setSettings({
        maintenance_mode: { enabled: false, message: '' },
        live_ticker: { enabled: false, url: '', text: 'Watch Live Now' },
        withdrawal_limits: { min_amount_pence: 10000, max_amount_pence: 1000000 },
        hero_content: {
          title: 'Win Premium Baby Gear Instantly',
          description: 'Enter our instant win competitions for a chance to win iCandy prams, car seats, and cash prizes. Over 1,900 instant wins available now.'
        },
        featured_partner: {
          enabled: true,
          mode: 'auto',
          manual_partner_id: null
        },
        email_logo: {
          url: null
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const updateSetting = async (key: string, value: Json) => {
    try {
      const { error } = await supabase.rpc('update_system_setting', {
        key,
        value
      })

      if (error) throw error

      // Reload settings to ensure UI is in sync
      await loadSettings()
    } catch (error) {
      console.error('Error updating system setting:', error)
      throw error
    }
  }

  return {
    settings,
    loading,
    updateSetting,
    maintenanceMode: settings?.maintenance_mode,
    liveTicker: settings?.live_ticker,
    withdrawalLimits: settings?.withdrawal_limits,
    heroContent: settings?.hero_content,
    featuredPartner: settings?.featured_partner,
    emailLogo: settings?.email_logo
  }
}
